import React, { FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  ThemeProvider,
  Theme,
  StyledEngineProvider,
  Switch,
  createTheme,
  adaptV4Theme,
} from '@mui/material';
import * as yup from 'yup';

import Modal from '../../../Modal';
import { SelectElement, OptionElement } from '../../../SelectElement';
import { NewTagFormValues, NewTagOrLabelModalProps } from './interfaces';
import { NoteFieldKind } from '../../../../shared/db/note-extra-fields-db';
import styles from './NewTagOrLabelModal.module.scss';
import { Button, ButtonSize, ButtonStyles } from '../../../Button';



const schema = yup.object().shape({
  tagName: yup.string().required('This field is required'),
});

const SWITCH_THEME = createTheme(adaptV4Theme({
  overrides: {
    MuiSwitch: {
      track: {
        '$checked$checked + &': {
          opacity: 1.0,
          backgroundColor: 'rgb(255, 124, 2)',
        },
      },
    },
  },
}));

const FIELD_TYPE_OPTIONS: { value: NoteFieldKind; text: string }[] = [
  { value: 'select', text: 'Text with suggestion' },
  { value: 'text', text: 'Text' },
  { value: 'number', text: 'Number' },
  { value: 'money', text: 'Money' },
  { value: 'boolean', text: 'Yes / No' },
  { value: 'textarea', text: 'Text area' },
];

function getModalTitle(isTagType: boolean, isEditMode: boolean): string {
  if (isTagType) return isEditMode ? 'Edit Tag' : 'Create New Tag';
  return isEditMode ? 'Edit Label' : 'Create New Label';
}

const NewTagOrLabelModal: FunctionComponent<NewTagOrLabelModalProps> = ({
  onClose,
  handleSubmit: onModalSubmit,
  isTagType,
  initialValues,
  onDelete,
}) => {
  const isEditMode = !!initialValues;
  const isLabelType = !isTagType;

  // --- Form ---
  const { register, handleSubmit, errors } = useForm<NewTagFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      tagName: initialValues?.tagName ?? '',
      kind: initialValues?.kind ?? 'select',
    },
  });

  // --- State ---
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLabelVisible, setIsLabelVisible] = useState(true);
  const [kind, setKind] = useState<NoteFieldKind>(initialValues?.kind ?? 'select');

  useEffect(() => {
    if (initialValues?.kind) setKind(initialValues.kind);
  }, [initialValues?.kind]);

  // --- Derived values ---
  const title = getModalTitle(!!isTagType, isEditMode);
  const submitLabel = isEditMode ? 'Save' : 'Create';
  const nameLabel = isTagType ? 'Tag Name' : 'Label Name';
  const namePlaceholder = isTagType ? 'Enter tag name' : 'Enter label name';

  // --- Handlers ---
  const onSubmit = (values: NewTagFormValues) => {
    const safeKind: NoteFieldKind = kind ?? 'select';
    setIsSubmitting(true);

    onModalSubmit({
      ...values,
      kind: safeKind,
      isLabelHidden: !isLabelVisible,
      isYesNoType: safeKind === 'boolean',
    })
      .then(onClose)
      .catch(() => setIsSubmitting(false));
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    onDelete?.();
  };

  return (
    <Modal onBackdropClick={onClose} className={styles.modal}>
      <div className={styles.card}>
        <h1 className={styles.title}>{title}</h1>

        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <label className={styles.label}>
            {nameLabel}
            <input
              type="text"
              id="tagName"
              name="tagName"
              ref={register()}
              className={styles.input}
              placeholder={namePlaceholder}
              maxLength={15}
              autoFocus
            />
            {errors.tagName && <p className={styles.error}>{errors.tagName.message}</p>}
          </label>

          {isLabelType && (
            <>
              <label className={styles.label}>
                Field type
                <div className={styles.selectWrapper}>
                  <SelectElement
                    value={kind}
                    onChange={({ target: { value } }) => setKind(value as NoteFieldKind)}
                    Component={OptionElement}
                    options={FIELD_TYPE_OPTIONS}
                  />
                </div>
              </label>

              {!isEditMode && (
                <label className={styles.switcher}>
                  <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={SWITCH_THEME}>
                      Keep New Label Visible
                      <Switch
                        checked={isLabelVisible}
                        onChange={(e) => setIsLabelVisible(e.target.checked)}
                        size="small"
                      />
                    </ThemeProvider>
                  </StyledEngineProvider>
                </label>
              )}
            </>
          )}

          <div className={styles.actions}>
            <div style={{ flex: 1 }}>
              {isEditMode && onDelete && (
                <Button
                  type="button"
                  styleType={ButtonStyles.RED_WHITE}
                  size={ButtonSize.SM}
                  onClick={handleDelete}
                  disabled={isSubmitting}
                >
                  Delete
                </Button>
              )}
            </div>

            <Button
              type="button"
              styleType={ButtonStyles.WHITE_GRAY}
              size={ButtonSize.SM}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              styleType={ButtonStyles.ORANGE_WHITE}
              size={ButtonSize.SM}
              disabled={isSubmitting}
            >
              {submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default NewTagOrLabelModal;